import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, ShieldAlert, AlertTriangle, CheckCircle2, Loader2,
  FileDown, Info, Search, Filter,
} from 'lucide-react';
import api, { privatiCleanupApi } from '../lib/api';
import { toast } from 'sonner';

const KIND_STYLE = {
  CERTO:     { label: 'Certo',     bg: 'bg-emerald-100', color: 'text-emerald-800', icon: CheckCircle2 },
  PROBABILE: { label: 'Probabile', bg: 'bg-amber-100',   color: 'text-amber-800',   icon: AlertTriangle },
  AMBIGUO:   { label: 'Ambiguo',   bg: 'bg-orange-100',  color: 'text-orange-800',  icon: AlertTriangle },
  CONFLITTO: { label: 'Conflitto', bg: 'bg-red-100',     color: 'text-red-800',     icon: ShieldAlert },
};

const KindBadge = ({ kind }) => {
  const s = KIND_STYLE[kind] || { label: kind, bg: 'bg-slate-100', color: 'text-slate-700', icon: AlertTriangle };
  const Ic = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.color}`}>
      <Ic size={10} /> {s.label}
    </span>
  );
};

const DuplicatesPrivatiPage = () => {
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filterKind, setFilterKind] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dismCleanup, setDismCleanup] = useState({ open: false, loading: false, preview: null, result: null });

  const openDismCleanup = async () => {
    setDismCleanup({ open: true, loading: true, preview: null, result: null });
    try {
      const { data } = await privatiCleanupApi.preview();
      setDismCleanup((s) => ({ ...s, loading: false, preview: data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
      setDismCleanup({ open: false, loading: false, preview: null, result: null });
    }
  };

  const applyDismCleanup = async () => {
    if (!window.confirm(`Archiviare ${dismCleanup.preview?.totale || 0} record PRIVATI la cui persona è già dismessa in un'azienda reale?\n\nBackup automatici verranno creati per workers + formazione + visite.`)) return;
    setDismCleanup((s) => ({ ...s, loading: true }));
    try {
      const { data } = await privatiCleanupApi.apply();
      setDismCleanup((s) => ({ ...s, loading: false, result: data }));
      toast.success(`${data.archiviati} PRIVATI archiviati · Backup: ${data.backup_collection}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setDismCleanup((s) => ({ ...s, loading: false }));
    }
  };

  const doScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/admin/duplicates/scan-privati', {});
      setScan(data);
      const c = data.counters || {};
      toast.success(`PRIVATI: ${c.pairs_certo || 0} CERTI · ${c.pairs_probabile || 0} PROBABILI · ${c.pairs_ambiguo || 0} AMBIGUI · ${c.pairs_conflitto || 0} CONFLITTI su ${c.workers_total || 0} lavoratori`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore scansione');
    } finally {
      setLoading(false);
    }
  };

  const doExport = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (scan?.token) params.set('token', scan.token);
      const resp = await api.get(`/admin/duplicates/privati/export.xlsx?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `duplicati-privati-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel esportato');
    } catch (e) {
      toast.error('Errore export');
    } finally {
      setDownloading(false);
    }
  };

  const allEntries = useMemo(() => {
    if (!scan) return [];
    const out = [];
    ['pairs_certo', 'pairs_probabile', 'pairs_ambiguo', 'pairs_conflitto'].forEach((k) => {
      (scan[k] || []).forEach((entry) => out.push(entry));
    });
    return out;
  }, [scan]);

  const filtered = useMemo(() => {
    let rows = allEntries;
    if (filterKind !== 'ALL') rows = rows.filter((e) => e.kind === filterKind);
    const s = search.trim().toLowerCase();
    if (s) {
      rows = rows.filter((e) => {
        const p = e.principal || {};
        const allNames = [
          `${p.lastName || ''} ${p.firstName || ''}`.toLowerCase(),
          ...(e.duplicates || []).map((d) => `${d.lastName || ''} ${d.firstName || ''}`.toLowerCase()),
        ].join(' ');
        return allNames.includes(s) || (p.fiscalCode || '').toLowerCase().includes(s);
      });
    }
    return rows;
  }, [allEntries, filterKind, search]);

  const c = scan?.counters || {};

  return (
    <div className="space-y-6" data-testid="duplicates-privati-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="dp-back">
            <ArrowLeft size={20} />
          </button>
          <div className="p-2.5 rounded-xl bg-orange-100 text-orange-700">
            <Users size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#023F80]">Duplicati PRIVATI</h1>
            <p className="text-sm text-slate-500">
              Scansione dell'azienda <code>PRIVATI</code> (~3.4k lavoratori senza CF/data nascita). <strong>Solo segnalazione</strong>: nessun merge automatico.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={doScan}
            disabled={loading}
            data-testid="dp-scan-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {loading ? 'Scansione…' : 'Avvia scansione'}
          </button>
          <button
            onClick={openDismCleanup}
            data-testid="dp-cleanup-dismessi-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-red-200 text-red-700 hover:bg-red-50"
            title="Trova e archivia i record PRIVATI la cui persona è già dismessa in un'azienda reale"
          >
            <ShieldAlert size={14} /> Pulisci PRIVATI dismessi altrove
          </button>
          <button
            onClick={doExport}
            disabled={downloading || !scan}
            data-testid="dp-export-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Esporta Excel
          </button>
        </div>
      </div>

      <div className="rounded-2xl border-l-4 border-orange-400 bg-orange-50/50 p-4 text-sm text-orange-900 flex items-start gap-2">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Modalità solo segnalazione</p>
          <p className="text-orange-800/80">
            L'apply massiva è <em>disabilitata</em> per PRIVATI. Ogni segnalazione va verificata manualmente
            perché i lavoratori privati spesso non hanno CF né data di nascita. Per unire due record utilizza il
            merge manuale dalla scheda azienda del lavoratore principale.
          </p>
        </div>
      </div>

      {scan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="rounded-2xl p-5 bg-slate-100">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Analizzati</div>
              <div className="mt-1 text-3xl font-extrabold text-slate-900">{c.workers_total || 0}</div>
            </div>
            <div className="rounded-2xl p-5 bg-emerald-100">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Certi</div>
              <div className="mt-1 text-3xl font-extrabold text-emerald-900">{c.pairs_certo || 0}</div>
            </div>
            <div className="rounded-2xl p-5 bg-amber-100">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">Probabili</div>
              <div className="mt-1 text-3xl font-extrabold text-amber-900">{c.pairs_probabile || 0}</div>
            </div>
            <div className="rounded-2xl p-5 bg-orange-100">
              <div className="text-[10px] uppercase tracking-widest text-orange-800 font-semibold">Ambigui</div>
              <div className="mt-1 text-3xl font-extrabold text-orange-900">{c.pairs_ambiguo || 0}</div>
            </div>
            <div className="rounded-2xl p-5 bg-red-100">
              <div className="text-[10px] uppercase tracking-widest text-red-800 font-semibold">Conflitti</div>
              <div className="mt-1 text-3xl font-extrabold text-red-900">{c.pairs_conflitto || 0}</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca cognome, nome, CF…"
                  data-testid="dp-search"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              {['ALL', 'CERTO', 'PROBABILE', 'AMBIGUO', 'CONFLITTO'].map((k) => (
                <button
                  key={k}
                  onClick={() => setFilterKind(k)}
                  data-testid={`dp-filter-${k}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    filterKind === k ? 'border-[#023F80] bg-indigo-50 text-[#023F80] font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {k === 'ALL' ? `Tutti · ${allEntries.length}` : `${KIND_STYLE[k].label} · ${(scan[`pairs_${k.toLowerCase()}`] || []).length}`}
                </button>
              ))}
              <span className="text-xs text-slate-500 ml-auto">
                <Filter size={12} className="inline mr-1" /> {filtered.length} righe
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-3 py-2 text-left">Kind</th>
                    <th className="px-3 py-2 text-left">Motivo</th>
                    <th className="px-3 py-2 text-left">Principale (id + nome)</th>
                    <th className="px-3 py-2 text-left">CF / nascita principale</th>
                    <th className="px-3 py-2 text-left">Duplicato/i</th>
                    <th className="px-3 py-2 text-left">Formazioni collegate</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry) => {
                    const p = entry.principal || {};
                    const dupsCount = (entry.duplicates || []).length;
                    return (
                      <tr key={entry.group_id} data-testid={`dp-row-${entry.group_id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                        <td className="px-3 py-2 align-top"><KindBadge kind={entry.kind} /></td>
                        <td className="px-3 py-2 align-top text-slate-600">{entry.reason}</td>
                        <td className="px-3 py-2 align-top">
                          <div className="font-semibold">{p.lastName} {p.firstName}</div>
                          <div className="text-[10px] font-mono text-slate-400">{p.id}</div>
                        </td>
                        <td className="px-3 py-2 align-top font-mono text-[10px] text-slate-500">
                          <div>{p.fiscalCode || '—'}</div>
                          <div className="text-slate-400">nato: {p.birthDate || '—'}</div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="text-[10px] text-slate-500 mb-1">{dupsCount} duplicato/i:</div>
                          {(entry.duplicates || []).slice(0, 3).map((d) => (
                            <div key={d.id} className="text-[11px] text-slate-700">
                              · {d.lastName} {d.firstName}
                              <span className="text-slate-400 font-mono ml-1 text-[10px]">
                                ({d.fiscalCode || 'CF —'} · nato {d.birthDate || '—'})
                              </span>
                            </div>
                          ))}
                          {dupsCount > 3 && <div className="text-[10px] text-slate-400">…+{dupsCount - 3}</div>}
                        </td>
                        <td className="px-3 py-2 align-top text-center">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">
                            {(entry.linked_to_move && entry.linked_to_move.formazione) || 0}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-xs text-slate-400">
                        Nessuna coppia trovata con questi filtri.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!scan && !loading && (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-sm text-slate-500">
          <Info size={22} className="mx-auto mb-2 text-slate-400" />
          Premi <strong>Avvia scansione</strong> per analizzare i ~3.400 lavoratori dell'azienda PRIVATI e identificare eventuali duplicati.
        </div>
      )}

      {dismCleanup.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="dp-dism-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDismCleanup({ open: false, loading: false, preview: null, result: null })} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[min(96vw,640px)] max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-red-800 flex items-center gap-2">
                  <ShieldAlert size={18} /> Pulizia PRIVATI dismessi altrove
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Trova i record di <code>PRIVATI</code> la cui persona è già dismessa in un'azienda reale e li archivia.
                </p>
              </div>
              <button onClick={() => setDismCleanup({ open: false, loading: false, preview: null, result: null })} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {dismCleanup.loading && (
                <div className="text-center py-8 text-slate-500 text-sm">
                  <Loader2 size={20} className="inline animate-spin mr-2" /> Elaboro…
                </div>
              )}
              {!dismCleanup.loading && dismCleanup.preview && !dismCleanup.result && (
                <>
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Da archiviare</div>
                    <div className="text-3xl font-extrabold text-red-800 mt-1">{dismCleanup.preview.totale}</div>
                  </div>
                  {dismCleanup.preview.totale > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Primi 30</div>
                      <div className="rounded-lg border border-slate-200 max-h-64 overflow-y-auto text-xs divide-y divide-slate-100">
                        {(dismCleanup.preview.sample || []).map((s, i) => (
                          <div key={i} className="px-3 py-1.5">
                            <div className="font-semibold text-slate-800">{s.nome}</div>
                            <div className="text-[10px] font-mono text-slate-400">CF {s.cf || '—'} · dismesso in <span className="text-slate-600">{s.match_company}</span>{s.match_count > 1 ? ` (+${s.match_count - 1} altri)` : ''}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {dismCleanup.result && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Fatto</div>
                  <div className="text-sm text-emerald-900 mt-1">Archiviati <strong>{dismCleanup.result.archiviati}</strong> record PRIVATI</div>
                  {dismCleanup.result.backup_collection && (
                    <div className="text-[10px] font-mono text-emerald-800 mt-1">Backup: {dismCleanup.result.backup_collection}</div>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setDismCleanup({ open: false, loading: false, preview: null, result: null })} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Chiudi</button>
              {dismCleanup.preview && dismCleanup.preview.totale > 0 && !dismCleanup.result && (
                <button onClick={applyDismCleanup} disabled={dismCleanup.loading} data-testid="dp-dism-apply" className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white disabled:opacity-60 flex items-center gap-2">
                  {dismCleanup.loading ? <><Loader2 size={14} className="animate-spin" /> Archivio…</> : <><ShieldAlert size={14} /> Archivia i {dismCleanup.preview.totale}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicatesPrivatiPage;